import { computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useCartorioStore } from '@/stores/cartorio';
import { usePFStore } from '@/stores/pf';

export function useTipoUsuario() {
  const authStore = useAuthStore();
  const cartorioStore = useCartorioStore();
  const pfStore = usePFStore();

  /**
   * Detecta se o usuário é pessoa física (PF)
   * PF: usuário logado sem cartório associado OU com dados PF salvos
   */
  const isPF = computed(() => {
    // Verificar se tem dados PF salvos (alternância de perfil)
    const dadosPF = localStorage.getItem('dadosPF');
    if (dadosPF) {
      try {
        const pfData = JSON.parse(dadosPF);
        if (pfData.cpf && authStore.user?.cpf === pfData.cpf) {
          return true;
        }
      } catch (error) {
        console.error('Erro ao verificar dados PF:', error);
      }
    }
    
    // Caso padrão: usuário sem cartório associado
    return !cartorioStore.cartorioAtual && 
           authStore.user?.cpf && 
           authStore.user.cpf.length === 11;
  });

  /**
   * Detecta se o usuário é do tipo cartório
   * Cartório: usuário logado com cartório associado E não está em modo PF
   */
  const isCartorio = computed(() => {
    // Se está em modo PF, não é cartório
    if (isPF.value) {
      return false;
    }
    
    return cartorioStore.cartorioAtual && 
           cartorioStore.validarDadosCartorio(cartorioStore.cartorioAtual);
  });

  /**
   * Detecta se o usuário não tem permissão (nem PF nem cartório)
   */
  const semPermissao = computed(() => {
    return !isPF.value && !isCartorio.value;
  });

  /**
   * Retorna o tipo de usuário atual
   */
  const tipoUsuario = computed(() => {
    if (isPF.value) return 'PF';
    if (isCartorio.value) return 'CARTORIO';
    return 'SEM_PERMISSAO';
  });

  /**
   * Retorna o identificador do usuário (CPF ou CNS)
   */
  const identificadorUsuario = computed(() => {
    if (isPF.value) return authStore.user?.cpf;
    if (isCartorio.value) return cartorioStore.cnsAtual;
    return null;
  });

  /**
   * Retorna o nome do usuário/cartório
   */
  const nomeUsuario = computed(() => {
    if (isPF.value) return authStore.user?.name;
    if (isCartorio.value) return cartorioStore.nomeCartorio;
    return null;
  });

  /**
   * Retorna a função do usuário (apenas para cartório)
   */
  const funcaoUsuario = computed(() => {
    if (isCartorio.value) return cartorioStore.funcaoCartorio;
    if (isPF.value) return 'Pessoa Física';
    return null;
  });

  /**
   * Verifica se o usuário tem permissão para acessar o sistema
   */
  const temPermissao = computed(() => {
    return isPF.value || isCartorio.value;
  });

  /**
   * Retorna a rota inicial baseada no tipo de usuário
   */
  const rotaInicial = computed(() => {
    //if (isPF.value) return '/pf/home';
    if (isPF.value) return '/';
    if (isCartorio.value) return '/';
    return '/sem-permissao';
  });

  /**
   * Retorna informações completas do usuário
   */
  const informacoesUsuario = computed(() => {
    return {
      tipo: tipoUsuario.value,
      identificador: identificadorUsuario.value,
      nome: nomeUsuario.value,
      funcao: funcaoUsuario.value,
      temPermissao: temPermissao.value,
      rotaInicial: rotaInicial.value,
      isPF: isPF.value,
      isCartorio: isCartorio.value,
      semPermissao: semPermissao.value
    };
  });

  return {
    // Computed properties
    isPF,
    isCartorio,
    semPermissao,
    tipoUsuario,
    identificadorUsuario,
    nomeUsuario,
    funcaoUsuario,
    temPermissao,
    rotaInicial,
    informacoesUsuario
  };
}
